import { LuGripVertical, LuLayoutGrid, LuGlobe, LuCircleHelp } from 'react-icons/lu';
import { Box } from '@/components/ui/lib/box';
import { Button } from '@/components/ui/lib/button';
import { IconButton } from '@/components/ui/lib/icon-button';
import { Popover } from '@/components/ui/lib/popover';
import { Portal } from '@/components/ui/lib/portal';
import { Text } from '@/components/ui/lib/text';
import { Tooltip } from '@/components/ui/lib/tooltip';
import { createIcon } from '@/components/ui/lib/icon-utils';
import { useDisclosure } from '@/components/ui/lib/use-disclosure';
import { DICTIONARY } from '@/config/dictionary';
import { env } from '@/config/env';
import type { Tile } from '@/app/components/app-launcher-types';
import '@/app/components/app-launcher.css';

const TcapIcon = createIcon({
  displayName: 'TcapIcon',
  viewBox: '0 0 40 40',
  path: (
    <>
      {/* Blue rounded square — top-left */}
      <rect x="2" y="2" width="20" height="20" rx="4" ry="4" fill="#38bdf8" />
      {/* Green triangle — bottom-left */}
      <polygon points="2,38 22,38 12,22" fill="#4ade80" />
      {/* Purple circle — center overlapping */}
      <circle cx="24" cy="24" r="14" fill="#a855f7" />
    </>
  ),
});

const AppLauncher = () => {
  const { open, onToggle, onClose } = useDisclosure();

  const tiles: Tile[] = [
    {
      key: 'tcap',
      label: DICTIONARY.appLauncher.tiles.tcap,
      tooltip: DICTIONARY.appLauncher.tileTooltips.tcap,
      icon: <TcapIcon width="22px" height="22px" />,
      active: false,
      onClick: () => {
        window.open(env.VITE_TCAP_PORTAL_URL, '_blank', 'noopener,noreferrer');
        onClose();
      },
    },
    {
      key: 'wholesaler-management',
      label: DICTIONARY.appLauncher.tiles.wholesalerManagement,
      tooltip: DICTIONARY.appLauncher.tileTooltips.wholesalerManagement,
      icon: <LuGlobe size={22} />,
      active: true,
      onClick: () => undefined,
    },
    {
      key: 'help-center',
      label: DICTIONARY.appLauncher.tiles.helpCenter,
      tooltip: DICTIONARY.appLauncher.tileTooltips.helpCenter,
      icon: <LuCircleHelp size={22} />,
      active: false,
      onClick: () => {
        window.open(env.VITE_HELP_CENTER_URL, '_blank', 'noopener,noreferrer');
        onClose();
      },
    },
  ];

  return (
    <Popover.Root
      open={open}
      onOpenChange={({ open: isOpen }) => {
        if (!isOpen) onClose();
      }}
      autoFocus={false}
      positioning={{ placement: 'right-start', offset: { mainAxis: 8, crossAxis: 0 } }}
    >
      <Popover.Trigger asChild>
        <IconButton
          aria-label={DICTIONARY.appLauncher.triggerAriaLabel}
          variant="ghost"
          size="sm"
          className="text-muted hover:bg-subtle hover:text-base"
          onClick={onToggle}
        >
          <LuLayoutGrid className="h-4 w-4" />
        </IconButton>
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content
            width="280px"
            p="0"
            className="bg-surface border border-rim shadow-md"
            borderRadius="xl"
            overflow="hidden"
          >
            <Box className="app-launcher__header">
              <LuGripVertical size={14} className="text-muted" />
              <Text className="app-launcher__title">{DICTIONARY.appLauncher.title}</Text>
            </Box>
            <Box className="app-launcher__grid">
              {tiles.map((tile) => (
                <Tooltip
                  key={tile.key}
                  content={tile.tooltip}
                  positioning={{ placement: 'bottom' }}
                  openDelay={300}
                >
                  <Button
                    type="button"
                    variant="ghost"
                    h="auto"
                    w="full"
                    p="0"
                    aria-current={tile.active ? 'true' : undefined}
                    className={`app-launcher__tile${tile.active ? ' app-launcher__tile--active' : ''}`}
                    onClick={tile.onClick}
                  >
                    <Box className="app-launcher__tile-icon">{tile.icon}</Box>
                    <Text className="app-launcher__tile-label">{tile.label}</Text>
                  </Button>
                </Tooltip>
              ))}
            </Box>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
};

export default AppLauncher;
